import React, { useLayoutEffect } from 'react';
import { useTour } from './tourContext';
import './Tour.css';

export const TOUR_BUTTON_LABEL = 'Explore this page';
/** Visible text, so people can spot the guide without hovering. */
export const TOUR_BUTTON_TEXT = 'Explore';

interface Props {
  /** header: pill in the top bar. bar: compact, styled by `className` (e.g. the Present toolbar). floating: bottom-right corner. */
  variant?: 'header' | 'bar' | 'floating';
  className?: string;
  /** Runs before the tour starts (e.g. to close an open menu). */
  onStart?: () => void;
}

/** A compass: "find your way around this page". */
const CompassIcon: React.FC = () => (
  <svg className="tour-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <circle cx="12" cy="12" r="9" />
    <path d="M15.5 8.5l-2 5-5 2 2-5z" />
  </svg>
);

/** Explore: starts the tour of the current page. */
const TourButton: React.FC<Props> = ({ variant = 'header', className = '', onStart }) => {
  const ctx = useTour();
  const register = ctx?.registerInlineButton;
  useLayoutEffect(() => (variant === 'floating' || !register ? undefined : register()), [variant, register]);

  if (!ctx?.tour) return null;
  return (
    <button
      type="button"
      className={`tour-btn tour-btn--${variant} ${className}`.trim()}
      aria-label={TOUR_BUTTON_LABEL}
      aria-haspopup="dialog"
      aria-expanded={ctx.active}
      data-tour="tour-button"
      onClick={() => { onStart?.(); ctx.start(); }}
    >
      <CompassIcon />
      <span className="tour-btn__text" aria-hidden="true">{TOUR_BUTTON_TEXT}</span>
      {!ctx.explored && <span className="tour-btn__new" data-testid="tour-new" aria-hidden="true" />}
      <span className="tour-btn__tip" aria-hidden="true">{TOUR_BUTTON_LABEL}</span>
    </button>
  );
};

export default TourButton;
